import axios from 'axios';
import { IUserData, PlayInput } from 'Types';

/**
 * Reading and saving the account's answer to what the player plays on
 * (`play_input`): a MIDI keyboard, a piano or keyboard the app cannot hear, or
 * nothing yet. The answer has its own save routes; it is never part of the
 * settings write, so an older copy of the account cannot overwrite it.
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

export interface PlayInputOption {
  value: PlayInput;
  title: string;
}

/** The three answers, in the order every picker shows them. */
export const PLAY_INPUT_OPTIONS: readonly PlayInputOption[] = [
  { value: 'midi', title: 'A MIDI keyboard' },
  { value: 'unconnected_piano', title: 'A piano or keyboard without MIDI' },
  { value: 'none', title: 'Nothing yet, just exploring' },
];

/** Where the picker's question is asked after the device screen. */
export const PLAY_INPUT_QUESTION = 'What do you play on?';

/** Said where saving the answer failed; the old answer stays. */
export const SAVE_FAILED_MESSAGE = 'That did not save. Please try again.';

/** The keyboard-buying help, linked from the "Nothing yet" answer. */
export const KEYBOARD_BUYING_FAQ_URL = 'https://museflow.ai/faq';

const isPlayInput = (value: unknown): value is PlayInput =>
  PLAY_INPUT_OPTIONS.some((option) => option.value === value);

/**
 * True when the backend stores the answer: its account record carries the
 * answer field (null until answered) or the device question's own flag. A new
 * account's first record, the one sign-up creates, carries only the flag (the
 * create returns columns that have a default, and the answer has none). A
 * backend that does not store the answer leaves both out, and then the app
 * does not ask.
 */
export const accountStoresPlayInput = (userData: Partial<IUserData> | null | undefined): boolean =>
  userData != null && ('play_input' in userData || 'welcome_flow_setup_complete' in userData);

/** The answer as the picker shows it: an unanswered account plays on MIDI. */
export const shownPlayInput = (playInput: PlayInput | null | undefined): PlayInput =>
  playInput ?? 'midi';

/**
 * True when the player finished the welcome screen but has not answered the
 * device question. Only an explicit `false` counts: a backend that does not
 * store the flag leaves it out, and those players are never sent to it.
 */
export const needsDeviceSetup = (userData: Partial<IUserData> | null | undefined): boolean =>
  userData?.welcome_flow_setup_complete === false;

/** Where the app goes once the welcome screen is done. */
export const postWelcomeRoute = (userData: Partial<IUserData> | null | undefined): string =>
  needsDeviceSetup(userData) ? '/device-setup' : '/roadmap';

/**
 * Where a player who has not answered the device question is sent when they
 * reach `location`: the roadmap, where opening the app and signing in land,
 * asks the question first, as those entries do. The query goes along (a
 * payment's thank-you return keeps it for after the answer). A link into
 * anything else (a lesson, a tutorial, a piece, settings) opens as it is.
 * Null: stay.
 */
export const deviceSetupRedirect = (
  location: { pathname: string; search: string },
  userData: Partial<IUserData> | null | undefined,
): string | null =>
  accountStoresPlayInput(userData) &&
  needsDeviceSetup(userData) &&
  location.pathname.replace(/\/+$/, '') === '/roadmap'
    ? `/device-setup${location.search}`
    : null;

/**
 * How long a save of the answer may take before it counts as failed. Without
 * a limit, a request that is never answered would leave the player on
 * "saving" for good, with no way to try again.
 */
export const PLAY_INPUT_SAVE_TIMEOUT_MS = 10000;

const savedPlayInput = (response: { data?: Partial<IUserData> }, asked: PlayInput): PlayInput => {
  const stored = response.data?.play_input;
  return isPlayInput(stored) ? stored : asked;
};

/** Whether a failed save failed by running out of time (the write may still land). */
export const saveTimedOut = (error: unknown): boolean => {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT';
};

/** What the account holds now for the answer, read from the backend. */
export const readStoredAnswer = async (): Promise<Partial<IUserData>> => {
  const response = await axios.get<Partial<IUserData>>(`${baseUrl}user-data`, {
    timeout: PLAY_INPUT_SAVE_TIMEOUT_MS,
  });
  return {
    play_input: response.data?.play_input,
    welcome_flow_setup_complete: response.data?.welcome_flow_setup_complete,
  };
};

/** Save the answer from the picker, a prompt or the blocker. Resolves to the saved value. */
export const savePlayInput = async (playInput: PlayInput): Promise<PlayInput> => {
  if (!isPlayInput(playInput)) throw new Error(`Not a play input: ${String(playInput)}`);
  const response = await axios.put<Partial<IUserData>>(
    `${baseUrl}user-data/set-play-input?play_input=${playInput}`,
    {},
    { timeout: PLAY_INPUT_SAVE_TIMEOUT_MS },
  );
  return savedPlayInput(response, playInput);
};

/**
 * Save the device screen's answer together with the end of the welcome flow,
 * so the account never holds one without the other. Resolves to the saved value.
 */
export const saveDeviceSetup = async (playInput: PlayInput): Promise<PlayInput> => {
  if (!isPlayInput(playInput)) throw new Error(`Not a play input: ${String(playInput)}`);
  const response = await axios.put<Partial<IUserData>>(
    `${baseUrl}user-data/complete-device-setup?play_input=${playInput}`,
    {},
    { timeout: PLAY_INPUT_SAVE_TIMEOUT_MS },
  );
  return savedPlayInput(response, playInput);
};
